import { memo, useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import styles from "./Navbar.module.css";
import { Bell, User, LogOut, ChevronDown, Languages } from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { api } from "../../store/slices/authSlice";
import { useDispatch } from "react-redux";
import { logout } from "../../store/slices/authSlice";

const BASE_URL = "https://dashboard.rawkemya.com/api";

const navLinks = [
  { to: "/", labelKey: "topbar.dashboard" },
  { to: "/products", labelKey: "topbar.products" },
  { to: "/raw-materials", labelKey: "topbar.materials" },
  { to: "/top-formula", labelKey: "topbar.topFormula" },
  { to: "/Categories", labelKey: "topbar.categories" },
];

function Navbar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const [userName, setUserName] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);

  const isAuthenticated = !!localStorage.getItem("token");
  const currentLang = i18n.language?.startsWith("ar") ? "ar" : "en";

  const profileRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target))
        setProfileOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target))
        setNotifOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchProfile = async () => {
      try {
        const res = await api.get("/profile");
        if (res.data.code === 200) setUserName(res.data.data.name);
      } catch (err) {
        console.error("Profile fetch error:", err);
      }
    };
    fetchProfile();
  }, [isAuthenticated]);
  const truncateName = (name, maxChars = 6) => {
    if (!name) return "";
    return name.length > maxChars ? `${name.slice(0, maxChars)}..` : name;
  };

  const fetchNotifications = async () => {
    if (!isAuthenticated) return;
    setNotifLoading(true);
    try {
      const res = await api.get("/notifications");
      if (res.data.code === 200) setNotifications(res.data.data || []);
    } catch (err) {
      console.error("Notifications fetch error:", err);
    } finally {
      setNotifLoading(false);
    }
  };

  const handleNotifToggle = () => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    if (!notifOpen) fetchNotifications();
    setNotifOpen((prev) => !prev);
    setProfileOpen(false);
  };

  const handleProfileToggle = () => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    setProfileOpen((prev) => !prev);
    setNotifOpen(false);
  };

  const handleNavigation = (e, to) => {
    if (!isAuthenticated) {
      e.preventDefault();
      navigate("/login");
    }
    setMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await api.post("/logout");
    } catch (error) {
      console.error("Logout error:", error);
    }
    dispatch(logout());
    setProfileOpen(false);
    navigate("/login", { replace: true });
  };

  const handleNotifClick = (notif) => {
    const meta = notif.metadata;
    if (
      meta?.type === "supplier_material_purchase_order" &&
      meta?.purchase_order_id
    ) {
      setNotifOpen(false);
      navigate(`/orders/${meta.purchase_order_id}`);
    }
  };

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  const handleLanguageChange = (nextLang) => {
    i18n.changeLanguage(nextLang);
    localStorage.setItem("lang", nextLang);
    document.documentElement.dir = nextLang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = nextLang;
  };

  return (
    <>
      <header className={styles.topbar}>
        <div className={styles.mainRow}>
          <button
            className={styles.menuBtn}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="menu"
          >
            ☰
          </button>

          <div className={styles.brandSection}>
            <img
              src="/logo raw-kemya.png"
              alt="Raw-kemya Logo"
              className={styles.logo}
            />
          </div>

          <nav className={styles.links} aria-label={t("topbar.navigation")}>
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={(e) => handleNavigation(e, link.to)}
                className={({ isActive }) =>
                  isActive
                    ? `${styles.navLink} ${styles.active}`
                    : styles.navLink
                }
              >
                {t(link.labelKey)}
              </NavLink>
            ))}
          </nav>

          <div className={styles.right}>
            <div className={styles.notifWrapper} ref={notifRef}>
              <button
                className={styles.iconBtn}
                onClick={handleNotifToggle}
                aria-label={t("navbar.notifications")}
              >
                <Bell size={18} />
                {isAuthenticated && unreadCount > 0 && (
                  <span className={styles.notifBadge}>
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <div className={styles.notifDropdown}>
                  <div className={styles.notifHeader}>
                    <span>{t("navbar.notifications")}</span>
                  </div>
                  {notifLoading ? (
                    <div className={styles.notifEmpty}>
                      {t("navbar.loading")}
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className={styles.notifEmpty}>
                      {t("navbar.no_notifications")}
                    </div>
                  ) : (
                    <ul className={styles.notifList}>
                      {notifications.map((n, i) => {
                        const meta = n.metadata;
                        const isClickable =
                          meta?.type === "supplier_material_purchase_order" &&
                          meta?.purchase_order_id;
                        const orderId = meta?.purchase_order_id;
                        const statusLabel = meta?.status
                          ? meta.status.charAt(0).toUpperCase() +
                            meta.status.slice(1)
                          : null;

                        return (
                          <li
                            key={i}
                            className={`${styles.notifItem} ${!n.read_at ? styles.notifUnread : ""} ${isClickable ? styles.notifClickable : ""}`}
                            onClick={() => isClickable && handleNotifClick(n)}
                          >
                            <p>
                              {n.body ||
                                n.data?.message ||
                                n.message ||
                                (orderId && statusLabel
                                  ? `${t("navbar.order")} #PO-${orderId} ${t("navbar.has_been")} ${statusLabel}`
                                  : t("navbar.new_notification"))}
                            </p>
                            <div className={styles.notifMeta}>
                              {orderId && (
                                <span className={styles.notifOrderId}>
                                  PO-{orderId}
                                </span>
                              )}
                              {n.created_at && (
                                <span className={styles.notifTime}>
                                  {new Date(n.created_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className={styles.profileDropdown} ref={profileRef}>
              <button
                className={`${styles.profileBtn} ${isAuthenticated && userName ? styles.profileBtnAuth : ""}`}
                onClick={handleProfileToggle}
                aria-label={t("topbar.profile")}
              >
                {isAuthenticated && userName ? (
                  <>
                    <span className={styles.welcomeText} title={userName}>
                      {t("navbar.welcome")}{" "}
                      <strong>{truncateName(userName.split(" ")[0], 7)}</strong>
                    </span>
                    <ChevronDown
                      size={14}
                      className={profileOpen ? styles.chevronOpen : ""}
                    />
                  </>
                ) : (
                  <User size={18} />
                )}
              </button>

              {profileOpen && isAuthenticated && (
                <div className={styles.profileMenu}>
                  <button
                    onClick={() => {
                      navigate("/profile-menu");
                      setProfileOpen(false);
                    }}
                  >
                    <User size={16} /> {t("common.profile")}
                  </button>
                  <button onClick={handleLogout} className={styles.logout}>
                    <LogOut size={16} /> {t("common.logout")}
                  </button>
                </div>
              )}
            </div>

            <div className={`dropdown ${styles.langDropdown}`}>
              <button
                className={styles.langTrigger}
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <Languages size={18} className={styles.langIconMobile} />
                <span>
                  {currentLang === "ar"
                    ? t("navbar.language_ar")
                    : t("navbar.language_en")}
                </span>
                <ChevronDown size={14} />
              </button>

              <ul
                className={`dropdown-menu dropdown-menu-end ${styles.langMenu}`}
              >
                <li>
                  <button
                    className={`${styles.langItem} ${currentLang === "ar" ? styles.langItemActive : ""}`}
                    onClick={() => handleLanguageChange("ar")}
                  >
                    {t("navbar.language_ar")}
                  </button>
                </li>
                <li>
                  <button
                    className={`${styles.langItem} ${currentLang === "en" ? styles.langItemActive : ""}`}
                    onClick={() => handleLanguageChange("en")}
                  >
                    {t("navbar.language_en")}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className={styles.mobileMenu}>
          <button
            className={styles.closeBtn}
            onClick={() => setMenuOpen(false)}
          >
            ✕
          </button>
          <nav className={styles.mobileNav}>
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={(e) => handleNavigation(e, link.to)}
                className={({ isActive }) =>
                  isActive
                    ? `${styles.mobileLink} ${styles.mobileLinkActive}`
                    : styles.mobileLink
                }
              >
                {t(link.labelKey)}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </>
  );
}

export default memo(Navbar);
